/**
 * React hook for page tracking
 *
 * Provides easy integration with the page tracking service
 * Automatically captures booking state from React context
 * Automatically includes booking flow classification
 * Handles page lifecycle (mount/unmount)
 */

import { useEffect, useRef } from "react";
import {
    pageTrackingService,
    PageType,
    PageMetadata,
    PageContext,
    PageName,
} from "logger/page-tracking";
import { useTrackingMetadata } from "hooks/use-tracking-metadata";

interface UsePageTrackingOptions {
    pageName: PageName;
    pageType: PageType;
    metadata?: Partial<PageMetadata>;
    /**
     * Automatically track on mount/unmount
     * @default true
     */
    autoTrack?: boolean;
    /**
     * Dependencies that trigger re-tracking when changed
     */
    dependencies?: React.DependencyList;
}

/**
 * Hook for tracking page views with automatic lifecycle management
 *
 * @example
 * ```tsx
 * function SelectGuests() {
 *   const bookingState = useBookingStateContext();
 *
 *   usePageTracking({
 *     pageName: "GuestSelection",
 *     pageType: PageType.WizardStep,
 *     metadata: {
 *       guests: bookingState.guests,
 *       maxGuests: 50,
 *       bookingType: "simple"
 *     }
 *   });
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function usePageTracking({
    pageName,
    pageType,
    metadata = {},
    autoTrack = true,
    dependencies = [],
}: UsePageTrackingOptions): PageContext {
    // Extract feature from metadata if provided
    const metadataFeature = metadata.feature;
    const trackingMetadata = useTrackingMetadata(metadataFeature);

    const pageNameRef = useRef(pageName);
    const pageTypeRef = useRef(pageType);
    const metadataRef = useRef(metadata);

    // Merge tracking metadata with page metadata
    const enrichedMetadata = {
        ...metadata,
        flow: trackingMetadata.flow,
        feature: trackingMetadata.feature,
    };

    // Update refs when values change
    pageNameRef.current = pageName;
    pageTypeRef.current = pageType;
    metadataRef.current = enrichedMetadata;

    // Track page view on mount or when dependencies change
    useEffect(() => {
        if (autoTrack) {
            pageTrackingService.trackPageView(
                pageNameRef.current,
                pageTypeRef.current,
                metadataRef.current
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoTrack, trackingMetadata.flow, trackingMetadata.feature, ...dependencies]);

    // Get current context for use in component
    return pageTrackingService.getCurrentContext();
}
